import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';

import { useWorkersService } from '@restworld/data-services';
import { useAuth } from '@restworld/feature-authentication';
import { Worker } from '@restworld/utility-types';

import { useFetchWorkerData, useFetchWorkerDetailsQueryKey } from '../../../hooks';
import { setWorkerAtom } from '../../global-state.worker-data';

export function useWorkerExperienceToggle() {
  const queryClient = useQueryClient();
  const workersService = useWorkersService();
  const setWorkerDetails = useSetAtom(setWorkerAtom);
  const {
    user: { id: workerId },
  } = useAuth();
  const queryKey = useFetchWorkerDetailsQueryKey();
  const previousData = useFetchWorkerData();

  return useMutation({
    mutationFn: async (workerHasNoExperience: boolean) =>
      (
        await workersService.updateWorker({
          workerId,
          params: {
            new_to_sector_having_no_experience: workerHasNoExperience,
          },
        })
      ).data,
    onMutate: async (workerHasNoExperience: boolean) => {
      await queryClient.cancelQueries({ queryKey });
      // Optimistically update the cache with the new value
      queryClient.setQueryData(queryKey, (oldData: Worker) => ({
        ...oldData,
        new_to_sector_having_no_experience: workerHasNoExperience,
      }));
      // Return the previous value in case of rollback
      return previousData;
    },
    onError: (_err, _workerHasNoExperience, previousData) => {
      console.error('Error updating worker experience toggle:', _err);
      // Rollback to the previous value in case of error
      queryClient.setQueryData(queryKey, previousData);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey });
      setWorkerDetails(data);
    },
  });
}
